// src/components/CreateInvoice/CreateInvoice.tsx
import React, { useRef, useState, useCallback } from "react";
import axios from "axios";
import { Modal, Button, Spinner } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import {
  FaCamera,
  FaUpload,
  FaCircleExclamation,
  FaFileArrowUp,
} from "react-icons/fa6";
import {
  FaSyncAlt
} from "react-icons/fa";
import Webcam from "react-webcam";
import "./CreateInvoice.scss";

interface CrearFacturaProps {
  show: boolean;
  handleClose: () => void;
}

const API_URL =
  "https://oxeqd2tpe1.execute-api.us-east-2.amazonaws.com/AnalyzeInvoice";

const CreateInvoice: React.FC<CrearFacturaProps> = ({ show, handleClose }) => {
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment"
  );
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);

  const capture = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video) return;
    setLoading(true);
    setTimeout(() => {
      const vw = video.videoWidth,
        vh = video.videoHeight;
      const boxW = vw * 0.6,
        boxH = vh * 0.8;
      const boxX = (vw - boxW) / 2,
        boxY = (vh - boxH) / 2;
      const off = document.createElement("canvas");
      off.width = boxW;
      off.height = boxH;
      const ctx = off.getContext("2d")!;
      ctx.drawImage(video, boxX, boxY, boxW, boxH, 0, 0, boxW, boxH);
      off.toBlob(
        (blob) => {
          if (blob) {
            const capturedFile = new File([blob], "capture.jpg", {
              type: "image/jpeg",
            });
            setSelectedFile(capturedFile);
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreview(reader.result as string);
              setLoading(false);
            };
            reader.readAsDataURL(capturedFile);
          } else {
            setLoading(false);
          }
        },
        "image/jpeg",
        0.9
      );
    }, 50);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setSelectedFile(file);
      setLoading(true);

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
          setLoading(false);
        };
        reader.readAsDataURL(file);
      } else if (file.type === "application/pdf") {
        setPreview(null);
        setLoading(false);
      } else {
        setSelectedFile(null);
        setPreview(null);
        setLoading(false);
      }
      e.target.value = "";
    },
    []
  );

  const retake = () => {
    if (sending) return;
    setSelectedFile(null);
    setPreview(null);
    setDone(false);
  };

  // Fire-and-forget upload
  const useIt = () => {
    if (!selectedFile || sending) return;
    setSending(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const localId: number = user?.local_id ?? 0;

      axios
        .post(
          API_URL,
          {
            file: base64,
            local_id: localId,
            user_id: user?.user_id ?? 0,
            file_type: selectedFile.type,
          },
          { headers: { "Content-Type": "application/json" } }
        )
        .catch((err) => {
          console.error("Failed to send invoice:", err);
        });

      // show the “done” message immediately
      setDone(true);
    };

    reader.readAsDataURL(selectedFile);
  };

  const close = () => {
    if (sending && !done) return; // wait until done
    setSelectedFile(null);
    setPreview(null);
    setLoading(false);
    setSending(false);
    setDone(false);
    handleClose();
  };

  const isPdf = selectedFile?.type === "application/pdf";
  const isImage = selectedFile?.type?.startsWith("image/");

  return (
    <Modal show={show} onHide={close} centered className="upload-invoice-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Nueva Factura</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={close} disabled={sending && !done}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          // 1) DONE STATE — highest priority
          <div className="notification-container">
            <p>
              La factura ha sido enviada, te avisaremos cuando esté lista para
              validación; esto puede tardar entre 1 y 5 minutos.
            </p>
          </div>
        ) : !selectedFile ? (
          // 2) CAMERA / CHOOSE FILE
          <div className="camera-like-container">
            <Webcam
              audio={false}
              ref={webcamRef}
              mirrored={facingMode === "user"}
              className="webcam-feed"
              videoConstraints={{ facingMode }}
            />
            <div className="invoice-frame" />
            {loading && (
              <div className="loading-overlay">
                <Spinner animation="border" role="status" />
              </div>
            )}
            {!loading && (
              <div className="invoice-action-buttons">
          
                <Button
                  className="scan-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <FaUpload />
                </Button>
                 <Button
                  className="scan-button"
                  onClick={capture}
                  disabled={sending}
                >
                  <FaCamera />
                </Button>
                {isMobile && (
                  <Button
                    className="scan-button"
                    onClick={() =>
                      setFacingMode((f) =>
                        f === "user" ? "environment" : "user"
                      )
                    }
                    disabled={sending}
                  >
                    <FaSyncAlt />
                  </Button>
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/*,.pdf"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        ) : (
          // 3) PREVIEW + UPLOAD BUTTON
          <div className="preview-container">
            <div className="camera-like-container">
              {loading ? (
                <div className="loading-overlay">
                  <Spinner animation="border" role="status" />
                </div>
              ) : isImage && preview ? (
                <img src={preview} className="invoice-img" alt="Preview" />
              ) : isPdf && selectedFile ? (
                <div className="pdf-selected-info">
                  <p>PDF seleccionado: {selectedFile.name}</p>
                  <p>Listo para enviar.</p>
                </div>
              ) : null}
            </div>
            <div className="preview-actions">
              <Button variant="none" onClick={retake} disabled={sending}>
                <FaCircleExclamation style={{ marginRight: "0.5rem" }} />
                Seleccionar otra
              </Button>
            </div>
            <Button
              className="upload-invoice-button"
              variant="none"
              onClick={useIt}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Enviando…
                </>
              ) : (
                <>
                  <FaFileArrowUp style={{ marginRight: "0.5rem" }} /> Subir
                  archivo
                </>
              )}
            </Button>
          </div>
        )}
      </Modal.Body>
      {done && (
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button variant="none" className="form-button-create" onClick={close}>
              Cerrar
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CreateInvoice;
